import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '@/components/Hero';
import BarCard from '@/components/BarCard';
import LanguageToggle from '@/components/LanguageToggle';
import SEO from '@/components/SEO';
import { BARS } from '@/data/bars';
import { useLanguage } from '../context/LanguageContext';

export default function Home() {
  const { t } = useLanguage();

  // Specifically select the 3 featured bars requested for the ItemList schema
  const featuredIds = ['tapas', 'cocktails', 'pint'];
  const featuredBars = featuredIds
    .map(id => BARS.find(b => b.id === id))
    .filter(Boolean);

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "The Best Bars in Barberà del Vallès | Premium Directory",
    "description": "Discover the top-rated Tapas, Cocktails, and Pints in Barberà. No tourist traps, just the number one spot in each category.",
    "url": "https://barberadelvalles.bar",
    "numberOfItems": featuredBars.length,
    "itemListElement": featuredBars.map((bar, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "BarOrPub",
        "name": bar.name,
        "description": bar.description,
        "url": `https://barberadelvalles.bar/menu/${bar.id}`,
        "image": bar.image,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": bar.address.split(',')[0],
          "addressLocality": "Barberà del Vallès",
          "addressRegion": "Barcelona",
          "postalCode": "08210",
          "addressCountry": "ES"
        },
        "telephone": bar.phone,
        "priceRange": "$$"
      }
    }))
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 selection:bg-zinc-100 selection:text-zinc-950 pb-40">
      <SEO 
        title="The Best Bars in Barberà del Vallès | Premium Directory" 
        description="Discover the top-rated Tapas, Cocktails, and Pints in Barberà. No tourist traps, just the number one spot in each category."
        schema={homeSchema}
      />
      
      <LanguageToggle />
      <Hero />

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20" role="main">
        <header className="flex flex-col items-center mb-24 gap-6">
          <div className="w-px h-24 bg-gradient-to-b from-transparent via-zinc-800 to-zinc-800" aria-hidden="true" />
          <h2 className="text-[10px] font-bold tracking-[0.6em] text-zinc-600 uppercase ml-[0.6em]">
            {t.ui.selection}
          </h2>
        </header>

        <section aria-label="Bar Directory" className="space-y-24 md:space-y-40">
          {BARS.map((bar, index) => (
            <article key={bar.id}>
              <BarCard bar={bar} index={index} />
            </article>
          ))}
        </section>
      </main>

      <footer className="mt-56 text-center pb-20 flex flex-col items-center gap-8">
        <div className="w-16 h-px bg-zinc-900" aria-hidden="true" />
        <p className="text-zinc-700 text-[10px] tracking-[0.5em] uppercase font-bold ml-[0.5em] mb-4">
          {t.ui.footer}
        </p>
        <nav className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-[10px] tracking-widest uppercase font-bold text-zinc-600">
          <Link to="/journal" className="hover:text-zinc-400 transition-colors">
            {t.ui.journal}
          </Link>
          <Link to="/imprint" className="hover:text-zinc-400 transition-colors">
            {t.ui.imprint}
          </Link>
          <Link to="/privacy" className="hover:text-zinc-400 transition-colors">
            {t.ui.privacy}
          </Link>
        </nav>
      </footer>
    </div>
  );
}